import { useState } from 'react'
import { Plus, Trash2, Activity, Copy, Check } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useWebhookEndpoints,
  useCreateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useToggleWebhookEndpoint,
  usePingWebhookEndpoint,
} from '@/hooks/use-outbound-webhooks'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import type { WebhookEndpoint } from '@/lib/types'

const formSchema = z.object({
  url: z.string().url('URL inválida'),
  events: z.array(z.string()).min(1, 'Selecione pelo menos um evento'),
  description: z.string().optional(),
  active: z.boolean(),
})

const AVAILABLE_EVENTS = [
  { id: 'coupon.created', label: 'Cupom criado', description: 'Quando um novo cupom é gerado' },
  {
    id: 'referral.created',
    label: 'Indicação criada',
    description: 'Quando uma venda é atribuída',
  },
  {
    id: 'referral.rejected',
    label: 'Indicação rejeitada',
    description: 'Quando uma indicação é cancelada',
  },
  {
    id: 'wallet.balance_available',
    label: 'Saldo liberado',
    description: 'Quando a garantia expira',
  },
  { id: 'payout.requested', label: 'Saque solicitado', description: 'Quando alguém pede saque' },
]

// Mapa para traduzir eventos técnicos para humanos
const EVENT_LABELS: Record<string, string> = {
  'coupon.created': 'Cupom criado',
  'referral.created': 'Indicação criada',
  'referral.rejected': 'Indicação rejeitada',
  'wallet.balance_available': 'Saldo liberado',
  'payout.requested': 'Saque solicitado',
}

export function WebhookEndpointList() {
  const { data: endpoints, isLoading } = useWebhookEndpoints()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createdSecret, setCreatedSecret] = useState<string | null>(null)

  if (isLoading) {
    return <WebhookListSkeleton />
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center">
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <ChonkyButton size="sm">
              <Plus className="mr-2 h-4 w-4" /> Novo Destino
            </ChonkyButton>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Adicionar novo destino</DialogTitle>
              <DialogDescription>
                Informe a URL do seu sistema que receberá as notificações.
              </DialogDescription>
            </DialogHeader>
            <WebhookEndpointForm
              onSuccess={(secret) => {
                setIsCreateOpen(false)
                if (secret) setCreatedSecret(secret)
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Secret Reveal Dialog */}
        <Dialog open={!!createdSecret} onOpenChange={(open) => !open && setCreatedSecret(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Segredo de Assinatura (Signing Secret)</DialogTitle>
              <DialogDescription>
                Este segredo é usado para validar que os eventos vieram do Rafobase. Copie-o agora,
                pois <strong>você não poderá vê-lo novamente.</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center space-x-2 mt-4">
              <div className="grid flex-1 gap-2">
                <Input readOnly value={createdSecret || ''} className="font-mono bg-muted" />
              </div>
              <CopyToClipboardButton text={createdSecret || ''} />
            </div>
            <DialogFooter className="sm:justify-start">
              <ChonkyButton variant="secondary" onClick={() => setCreatedSecret(null)}>
                Entendi, já copiei
              </ChonkyButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>URL</TableHead>
            <TableHead>Eventos</TableHead>
            <TableHead className="w-[100px] text-center">Status</TableHead>
            <TableHead className="w-[150px] text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {endpoints?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-16 text-muted-foreground">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Activity className="h-8 w-8 text-muted-foreground/50" />
                  <p>Nenhum destino configurado ainda</p>
                  <ChonkyButton variant="secondary" size="sm" onClick={() => setIsCreateOpen(true)}>
                    Adicionar primeiro destino
                  </ChonkyButton>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            endpoints?.map((ep) => <WebhookEndpointRow key={ep.id} endpoint={ep} />)
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function WebhookEndpointRow({ endpoint }: { endpoint: WebhookEndpoint }) {
  const deleteMutation = useDeleteWebhookEndpoint()
  const toggleMutation = useToggleWebhookEndpoint()
  const pingMutation = usePingWebhookEndpoint()
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)

  return (
    <>
      <TableRow>
        <TableCell className="font-mono text-xs max-w-[300px]">
          <div className="flex items-center gap-2">
            <span className="truncate" title={endpoint.url}>
              {endpoint.url}
            </span>
            <CopyToClipboardButton text={endpoint.url} size="icon-sm" />
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            {endpoint.events.includes('*') ? (
              <Badge variant="outline">Todos os eventos</Badge>
            ) : (
              endpoint.events.map((evt) => (
                <Badge key={evt} variant="secondary" className="text-[10px]">
                  {EVENT_LABELS[evt] || evt}
                </Badge>
              ))
            )}
          </div>
        </TableCell>
        <TableCell className="text-center">
          <div className="flex justify-center">
            <Switch
              checked={endpoint.active}
              onCheckedChange={(checked) =>
                toggleMutation.mutate({ id: endpoint.id, active: checked })
              }
              disabled={toggleMutation.isPending}
            />
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => pingMutation.mutate(endpoint.id)}
              disabled={pingMutation.isPending}
              title="Enviar evento de teste"
            >
              <Activity className={`h-4 w-4 ${pingMutation.isPending ? 'animate-pulse' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setShowDeleteAlert(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente o endpoint e parará o
              envio de eventos para <strong>{endpoint.url}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate(endpoint.id)}
            >
              {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function WebhookEndpointForm({ onSuccess }: { onSuccess: (secret?: string) => void }) {
  const createMutation = useCreateWebhookEndpoint()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: '',
      events: [],
      description: '',
      active: true,
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    createMutation.mutate(values, {
      onSuccess: (response) => {
        form.reset()
        // Assuming response contains the new endpoint with secret
        onSuccess(response?.secret)
      },
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL de Destino</FormLabel>
              <FormControl>
                <Input placeholder="https://api.seu-sistema.com/webhook" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="events"
          render={() => (
            <FormItem>
              <FormLabel className="mb-4 block">Eventos Inscritos</FormLabel>
              <div className="flex flex-col gap-2">
                {AVAILABLE_EVENTS.map((item) => (
                  <FormField
                    key={item.id}
                    control={form.control}
                    name="events"
                    render={({ field }) => {
                      return (
                        <FormItem key={item.id} className="space-y-0">
                          <FormLabel className="flex flex-row items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer border border-transparent hover:border-border transition-colors w-full font-normal">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(item.id)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...field.value, item.id])
                                    : field.onChange(
                                        field.value?.filter((value) => value !== item.id)
                                      )
                                }}
                              />
                            </FormControl>
                            <div className="flex flex-1 items-center gap-2 text-sm">
                              <span>{item.label}</span>
                              <code className="text-[10px] text-foreground/70 bg-muted px-1.5 py-0.5 rounded font-mono">
                                {item.id}
                              </code>
                            </div>
                          </FormLabel>
                        </FormItem>
                      )
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-4">
          <ChonkyButton
            type="submit"
            disabled={createMutation.isPending}
            loading={createMutation.isPending}
          >
            Criar Endpoint
          </ChonkyButton>
        </div>
      </form>
    </Form>
  )
}

function WebhookListSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>
      <div className="border rounded-md bg-card shadow-sm">
        <div className="p-4 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-8 w-24" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CopyToClipboardButton({
  text,
  size = 'icon',
}: {
  text: string
  size?: 'icon' | 'icon-sm'
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success('Copiado para a área de transferência')
    setTimeout(() => setCopied(false), 2000)
  }

  if (size === 'icon-sm') {
    return (
      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
        {copied ? (
          <Check className="h-3 w-3 text-green-500" />
        ) : (
          <Copy className="h-3 w-3 text-muted-foreground" />
        )}
      </Button>
    )
  }

  return (
    <Button size="icon" variant="ghost" onClick={handleCopy}>
      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
    </Button>
  )
}
